import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import { searchLegalFiles } from "../../services/LegalFileService";

/**
 * Buscador de expedientes por código o nombre.
 *
 * Se usa tanto en la navbar (variant="navbar") como en la pantalla
 * "Buscar Expediente" (variant="page"), para que el comportamiento sea
 * exactamente el mismo en los dos lugares.
 *
 * Al seleccionar un resultado abre el detalle en la misma pestaña.
 */
export default function LegalFileSearch({
  variant = "page",
  autoFocus = false,
  placeholder = "Buscar expediente por código o nombre…",
  onSelected,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const isNavbar = variant === "navbar";

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const term = inputValue.trim();

  useEffect(() => {
    if (term.length < 2) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const timeout = setTimeout(async () => {
      try {
        const res = await searchLegalFiles({ perPage: 8, search: term });
        setOptions(res.data?.data || []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [term]);

  const handleSelect = (legalFile) => {
    if (!legalFile?.id) return;

    navigate(`/legal/file/${legalFile.id}`);

    setInputValue("");
    setOptions([]);
    setOpen(false);
    onSelected?.(legalFile);
  };

  const noOptionsText = useMemo(() => {
    if (term.length < 2) return "Escribí al menos 2 caracteres";
    return loading ? "Buscando…" : "Sin resultados";
  }, [term, loading]);

  const fieldBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.82)";
  const fieldBgHover = isDark
    ? "rgba(255,255,255,0.10)"
    : "rgba(255,255,255,0.98)";

  return (
    <Autocomplete
      open={open && term.length >= 2}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={null}
      blurOnSelect
      clearOnBlur={false}
      handleHomeEndKeys={false}
      inputValue={inputValue}
      onInputChange={(_, newValue, reason) => {
        if (reason === "input" || reason === "clear") setInputValue(newValue);
      }}
      onChange={(_, option) => handleSelect(option)}
      options={options}
      filterOptions={(x) => x}
      loading={loading}
      loadingText="Buscando expedientes…"
      noOptionsText={noOptionsText}
      getOptionLabel={(option) => option?.code || ""}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      fullWidth
      size={isNavbar ? "small" : "medium"}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            mt: 1,
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${
              isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"
            }`,
            background: isDark
              ? "rgba(15,23,42,0.97)"
              : "rgba(255,255,255,0.98)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            boxShadow: "0 18px 50px rgba(0,0,0,0.16)",
            "& .MuiAutocomplete-listbox": { p: 0.6, maxHeight: 420 },
          },
        },
      }}
      PaperComponent={(props) => <Paper {...props} />}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const lawyer = option.lawyers?.[0];
        const lawyerName = lawyer
          ? `${lawyer.name || ""} ${lawyer.lastname || ""}`.trim()
          : null;

        const meta = [option.status, option.location, lawyerName]
          .filter(Boolean)
          .join(" · ");

        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{
              display: "flex !important",
              alignItems: "center !important",
              gap: 1.2,
              px: 1.2,
              py: 1,
              borderRadius: 2.5,
              "&:hover": {
                bgcolor: isDark
                  ? "rgba(25,118,210,0.16)"
                  : "rgba(25,118,210,0.08)",
              },
              '&[aria-selected="true"], &.Mui-focused': {
                bgcolor: isDark
                  ? "rgba(25,118,210,0.20)"
                  : "rgba(25,118,210,0.10)",
              },
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                bgcolor: "rgba(25,118,210,0.10)",
                color: "primary.main",
              }}
            >
              <FolderOpenRoundedIcon sx={{ fontSize: 19 }} />
            </Box>

            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.1 }}
              >
                <Chip
                  label={option.code}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11.5,
                    fontWeight: 900,
                    bgcolor: "rgba(25,118,210,0.12)",
                    color: "primary.main",
                    "& .MuiChip-label": { px: 0.9 },
                  }}
                />
              </Box>

              <Typography
                sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.35 }}
                noWrap
              >
                {option.name || "Sin nombre"}
              </Typography>

              {meta && (
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.62, display: "block" }}
                  noWrap
                >
                  {meta}
                </Typography>
              )}
            </Box>

            <ChevronRightRoundedIcon
              sx={{ fontSize: 16, opacity: 0.35, flexShrink: 0 }}
            />
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.4, mr: -0.2 }}>
                <SearchRoundedIcon sx={{ fontSize: 20, opacity: 0.45 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 999,
              bgcolor: fieldBg,
              transition: "background-color .18s ease, box-shadow .18s ease",
              "&:hover": { bgcolor: fieldBgHover },
              "&.Mui-focused": {
                bgcolor: fieldBgHover,
                boxShadow: "0 0 0 4px rgba(25,118,210,0.12)",
              },
              "& fieldset": {
                borderColor: isDark
                  ? "rgba(255,255,255,0.12)"
                  : "rgba(0,0,0,0.10)",
              },
              "&:hover fieldset": { borderColor: "rgba(25,118,210,0.34)" },
            },
            ...(isNavbar && {
              "& .MuiOutlinedInput-input": { fontSize: 13.5, py: 0.6 },
            }),
          }}
        />
      )}
    />
  );
}
